import { useTranslation } from "react-i18next";
import {
  attemptShowErrorsInSnackbar,
  useSnackbarActionContext,
} from "../context/SnackbarContext";
import { SnackbarType } from "../components/sharedComponents/Snackbar";
import { UserPreferenceKey } from "../components/routeComponents/UserManagement/UserPreference";
import { getStringDate } from "../components/routeComponents/Persons/HelperFunctions";
import { ApolloError, DocumentNode, gql, useMutation } from "@apollo/client";
import { GET_USER_PROFILE } from "./useGetUserProfile";

export enum ProfileActions {
  DETAILS = "details",
  PREFERENCES = "preferences",
  PASSWORD = "password",
}

const useProfileMutation = () => {
  const updatePersonMutation = useMutation(
    UPDATE_PERSON,
    UpdatePersonResponseHandling()
  );
  const updateUserMutation = useMutation(
    UPDATE_USER,
    ResponseHandling("profile_update_success", [GET_USER_PROFILE])
  );

  const updatePreferencesMutation = useMutation(
    SAVE_USER_PREFERRED,
    ResponseHandling(
      "userPreferences_user_preferences_were_successfully_updated",
      [GET_USER_PROFILE]
    )
  );

  const updatePasswordMutation = useMutation(
    UPDATE_PASSWORD,
    ResponseHandling(
      "userManagement_ChangePassword_your_password_has_been_successfully_updated",
      [GET_USER_PROFILE]
    )
  );

  const mutate = async (action: ProfileActions, input: any, userId: string) => {
    switch (action) {
      case ProfileActions.DETAILS: {
        const detailsInput = mapFormToDetailsInput(input as any, userId);
        const [updatePerson] = updatePersonMutation;
        const [updateUser] = updateUserMutation;

        await updatePerson({ variables: detailsInput.person });

        return await updateUser({ variables: detailsInput.user });
      }

      case ProfileActions.PREFERENCES: {
        const preferencesInput = mapFormToPreferencesInput(
          input as any,
          userId
        );

        const [preferences] = updatePreferencesMutation;

        return await preferences({ variables: { input: preferencesInput } });
      }

      case ProfileActions.PASSWORD:
        const passwordInput = mapFormToPasswordInput(input as any, userId);

        const [password] = updatePasswordMutation;

        return await password({ variables: passwordInput });

      default:
        throw new Error(`Invalid action: ${action}`);
    }
  };

  return {
    mutations: {
      mutate,
      isLoading:
        updatePersonMutation[1].loading ||
        updateUserMutation[1].loading ||
        updatePasswordMutation[1].loading ||
        updatePreferencesMutation[1].loading,
    },
  };
};

export default useProfileMutation;

function mapFormToDetailsInput(input: any, userId: string): any {
  const user = {
    userId,
    username: input.displayedLoginName,
    lastName: input.lastName,
    firstName: input.firstName,
    email: input.email,
    phone: !input.phone || input.phone.length === 0 ? null : input.phone,
  };

  const person = {
    input: {
      id: input.id,
      primaryLocation:
        typeof input.person?.primaryLocation === "string"
          ? input.person?.primaryLocation
          : input.person?.primaryLocation.value,
      relationshipToCustomer:
        typeof input.person?.relationshipToCustomer === "string"
          ? input.person.relationshipToCustomer
          : input.person.relationshipToCustomer.value,
      gender: input?.gender?.id,
      birthDate: getStringDate(input?.birthDate),
      streetAddress: input?.streetAddress,
      address2: input?.address2,
      city: input?.city,
      zipPostal: input?.zipPostal,
      stateProv: input?.stateProv,
      country: input?.country,
      currentPosition: input?.currentPosition,
      currentPositionStartDate: getStringDate(input?.currentPositionStartDate),
      yearsInJob: input?.yearsInJob,
    },
  };

  return {
    user,
    person,
  };
}

function mapFormToPreferencesInput(input: any, userId: string): any {
  return [
    {
      userId,
      key: UserPreferenceKey.language,
      value: input[UserPreferenceKey.language]
        ? input[UserPreferenceKey.language].value
        : "",
    },
    {
      userId,
      key: UserPreferenceKey.timeFormat,
      value: input[UserPreferenceKey.timeFormat]
        ? input[UserPreferenceKey.timeFormat].value
        : "",
    },
  ];
}

function mapFormToPasswordInput(input: any, userId: any): any {
  return {
    userId,
    newPassword: input.newPassword,
    currentPassword: input.password,
  };
}

function ResponseHandling(messageKey: string, queries: Array<DocumentNode>) {
  const { showSnackbar } = useSnackbarActionContext();
  const { t } = useTranslation();

  return {
    refetchQueries: queries,
    onCompleted: () => {
      showSnackbar(SnackbarType.Success, t(messageKey));
    },
    onError: (error: ApolloError) => {
      attemptShowErrorsInSnackbar(showSnackbar, error);
    },
    update: (cache) => {
      cache.evict({ id: "ROOT_QUERY", fieldName: "user" });
      cache.gc();
    },
  };
}

function UpdatePersonResponseHandling() {
  const { showSnackbar } = useSnackbarActionContext();

  return {
    onError: (error: ApolloError) => {
      attemptShowErrorsInSnackbar(showSnackbar, error);
    },
  };
}

export const UPDATE_PERSON = gql`
  mutation updatePerson($input: PersonUpdateInput) {
    updatePerson(input: $input) {
      contractingCompany {
        id
        name
        customer {
          id
        }
      }
      displayName
      email
      employeeID
      firstName
      id
      lastName
      middleName
      phone
      faxNumber
      yearsInJob
      relationshipToCustomer
      supervisor {
        id
      }
      userId
      personSensitive {
        gender
        streetAddress
        address2
        address3
        address4
        city
        zipPostal
        stateProv
        country
        currentPosition
        currentPositionStartDate
        birthDate
        hireDate
        effectiveDate
      }
    }
  }
`;

export const UPDATE_USER = gql`
  mutation handleUpdateUser(
    $userId: ID!
    $username: String
    $firstName: String!
    $lastName: String!
    $email: String
    $phone: String
  ) {
    updateUserAndDemographic(
      input: {
        userId: $userId
        username: $username
        firstName: $firstName
        lastName: $lastName
        email: $email
        phone: $phone
      }
    ) {
      id
    }
  }
`;

const SAVE_USER_PREFERRED = gql`
  mutation handleSaveUserPreferred($input: [UserPreferencesInput]) {
    saveUserPreferred(input: $input) {
      userId
      key
      value
    }
  }
`;

const UPDATE_PASSWORD = gql`
  mutation handleUpdatePassword(
    $userId: ID!
    $currentPassword: String!
    $newPassword: String!
  ) {
    updatePassword(
      userId: $userId
      currentPassword: $currentPassword
      newPassword: $newPassword
    )
  }
`;
